import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field, NSelect } from "@/components/common";
import { ServiceOptions } from "@/pages/QuoteForm";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { name: "", company: "", email: "", phone: "", service_id: "", location: "", message: "", website: "" };

export const RequestForm = ({ services, serviceId, setServiceId }) => {
  const [f, setF] = useState(EMPTY);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/public/requests", { ...f, service_id: serviceId || null });
      setSent(r.data.number);
      setF(EMPTY);
      setServiceId("");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent)
    return (
      <div className="rounded-md bg-white p-10 text-center" data-testid="request-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">Demande envoyée</h3>
        <p className="mt-2 text-sm text-slate-600">Votre demande <b className="font-mono">{sent}</b> a bien été reçue. Notre équipe commerciale vous recontacte rapidement.</p>
        <button onClick={() => setSent(null)} className="mt-6 text-sm font-semibold text-red-600 hover:underline" data-testid="request-new-btn">Envoyer une autre demande</button>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid gap-4 rounded-md bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="request-form">
      <Field label="Nom complet *"><Input required minLength={2} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="req-name-input" /></Field>
      <Field label="Entreprise / organisation"><Input value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="req-company-input" /></Field>
      <Field label="Téléphone *"><Input required minLength={6} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+243 …" data-testid="req-phone-input" /></Field>
      <Field label="Email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="req-email-input" /></Field>
      <Field label="Service souhaité" className="sm:col-span-2">
        <NSelect value={serviceId} onChange={setServiceId} testId="req-service-select">
          <option value="">— Choisir un service —</option>
          <ServiceOptions services={services} />
        </NSelect>
      </Field>
      <Field label="Lieu de la prestation" className="sm:col-span-2"><Input value={f.location} onChange={(e) => set("location", e.target.value)} placeholder="Ville, site, adresse…" data-testid="req-location-input" /></Field>
      <Field label="Description de votre besoin *" className="sm:col-span-2">
        <Textarea required minLength={10} rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="req-message-input" />
      </Field>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2" data-testid="req-error">{error}</p>}
      <button type="submit" disabled={loading} data-testid="req-submit-btn"
        className="group flex h-12 items-center justify-center gap-2 rounded-md bg-red-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer ma demande <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};
